import React, { useEffect, useState } from 'react';
import api from '../api/api';
import './css/VendorsPage.css';
import TopNav from '../components/TopNav';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from '../components/ConfirmModal';

function VendorsPage({ embedded = false } = {}) {
  const [vendors, setVendors] = useState([]);
  const [highlightedId, setHighlightedId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    services_offered: '',
    contact_info: '',
    balance: '',
    delivery: 'Shipping', // NEW: default
  });

  const [confirmState, setConfirmState] = useState({
    open: false,
    message: '',
    onConfirm: () => {},
  });

  const askConfirm = (message, onConfirm) => {
    setConfirmState({
      open: true,
      message,
      onConfirm: () => {
        onConfirm();
        setConfirmState({ open: false, message: '', onConfirm: () => {} });
      },
    });
  };

  const { user } = useAuth();
  const userRole = user?.role || '';

  useEffect(() => {
    document.title = "Vendors - Services Irene's Wigs.com";
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const res = await api.get('/vendors');
      setVendors(res.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/vendors', form);
      setForm({ name: '', services_offered: '', contact_info: '', balance: '', delivery: 'Shipping' });
      fetchVendors();
    } catch (error) {
      console.error('Error adding vendor:', error);
    }
  };

  const handleDelete = async (id) => {
    askConfirm('Are you sure you want to delete this vendor?', async () => {
      try {
        await api.delete(`/vendors/${id}`);
        fetchVendors();
      } catch (err) {
        console.error('Error deleting vendor:', err);
      }
    })
  };

  const handleVendorChange = (id, field, value) => {
    setVendors((prev) =>
      prev.map((vendor) =>
        vendor.id === id ? { ...vendor, [field]: value } : vendor
      )
    );
  };

  const handleUpdate = async (id) => {
    const vendor = vendors.find(p => p.id === id);
    try {
      await api.put(`/vendors/${id}`, vendor);
      setHighlightedId(id);
      fetchVendors();
      setTimeout(() => setHighlightedId(null), 2000);
    } catch (err) {
      console.error('Error updating vendor:', err);
    }
  };

  return (
    <>
      {!embedded && <TopNav />}
      <div className={embedded ? 'admin-dashboard-embedded-page admin-vendors-tab' : 'inventory-container'}>
        <h1 className="inventory-title">Vendors</h1>

        {/* Add vendor Form */}
        <form onSubmit={handleSubmit} className="inventory-form">
          <input
            type="text"
            disabled={userRole === 'employee'}
            placeholder="Vendor Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        
          <input
            type="text"
            disabled={userRole === 'employee'}
            placeholder="Contact Info"
            value={form.contact_info}
            onChange={(e) => setForm({ ...form, contact_info: e.target.value })}
          />

          {/* NEW: Delivery dropdown in form */}
          <select
            disabled={userRole === 'employee'}
            value={form.delivery}
            onChange={(e) => setForm({ ...form, delivery: e.target.value })}
            aria-label="Delivery Method"
          >
            <option value="Shipping">Shipping</option>
            <option value="Local">Local</option>
          </select>

          <input
            type="number"
            placeholder="Balance"
            value={form.balance}
            onChange={(e) => setForm({ ...form, balance: e.target.value })}
          />
          <button type="submit" disabled={userRole === 'employee'}>Add Vendor</button>
        </form>

        {/* vendor List */}
        <div className="inventory-list">
          {vendors.map((vendor) => (
            
            <div
              key={vendor.id}
              className={`inventory-card ${highlightedId === vendor.id ? 'highlight' : ''}`}
            >
              <div className="card-row inline-row">
                
                <div className="inline-field">
                  <strong>ID:</strong> {vendor.id}
                </div>
              </div>

              <div className="card-row">
                <strong>Name:</strong>
                <input
                  type="text"
                  disabled={userRole === 'employee'}
                  value={vendor.name}
                  onChange={(e) => handleVendorChange(vendor.id, 'name', e.target.value)}
                />
              </div>

              <div className="card-row">
                <strong>Contact Info:</strong>
                <input
                  type="text"
                  disabled={userRole === 'employee'}
                  value={vendor.contact_info}
                  onChange={(e) => handleVendorChange(vendor.id, 'contact_info', e.target.value)}
                />
              </div>
              {/* NEW: Delivery dropdown in each card */}
              <div className="card-row">
                <strong>Delivery:</strong>
                <select
                  disabled={userRole === 'employee'}
                  value={vendor.delivery || 'Shipping'}
                  onChange={(e) => handleVendorChange(vendor.id, 'delivery', e.target.value)}
                  aria-label={`Delivery method for vendor ${vendor.name}`}
                >
                  <option value="Shipping">Shipping</option>
                  <option value="Local">Local</option>
                </select>
              </div>

              <div className="card-row">
                <strong>Balance:</strong>
                <input
                    type="number"
                    min={0}
                    step="0.01"
                    disabled={userRole === 'employee'}
                    value={vendor.balance ?? ''}   // allow empty while editing
                    onChange={(e) =>
                        handleVendorChange(
                            vendor.id,
                            'balance',
                            e.target.value === '' ? '' : Number(e.target.value)
                        )
                    }
                />

              </div>


              <div className="vendor-card-row">
                <button onClick={() => handleUpdate(vendor.id)} className="inline-button update-button">Update</button>
                <button
                  disabled={userRole === 'employee'}
                  onClick={() => handleDelete(vendor.id)}
                  className="inline-button delete-button"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {confirmState.open && (
        <ConfirmModal
          open={confirmState.open}
          message={confirmState.message}
          onConfirm={confirmState.onConfirm}
          onCancel={() =>
            setConfirmState({ open: false, message: '', onConfirm: () => {} })
          }
        />
      )}
    </>
  );
}

export default VendorsPage;
